import { useLocation, useNavigate } from "react-router-dom"
import { DataReceiptsOrder } from "../api/DataReceiptApi"
import { nexDefaultPagination } from "../components/Table/pageable-table/NexPageSize"
import { Order } from "../models/SortOrder"
import { DataReceiptSearchFitlersAndPaging } from "../pages/DataReceipt/table-view/DataReceiptSearchFilters"
import {
    SensorInstallFiltersAndPaging,
    SensorInstallOrder,
} from "../pages/SensorInstallation/table/SensorInstallSearchFilters"
import { ConfirmationRoutes, routes } from "../routing/NexRoutes"
import { useDbStateStore } from "../zstore/DbStateStore"

export const useNexusNavigate = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const userMgmtUrl = useDbStateStore((state) => state.userMgmtUrl)

    return {
        goHome: () => navigate(routes.home),
        goToUserLogin: () =>
            window.location.replace(`${userMgmtUrl}authentication?redirected_from=${window.location.origin}`),
        confirmationPage: (
            route?: ConfirmationRoutes,
            values?: {
                entityName?: string
                projectNumber?: string
            },
        ) => navigate(routes.confirmation(route, values)),
        flightplanUpload: (projectNumber: string) => navigate(`/${projectNumber}${routes.flightplanUpload}`),
        flightplanEdit: (projectNumber: string) => navigate(`/${projectNumber}${routes.flightplanEditor}/edit`),
        missionUpload: (projectNumber: string) => navigate(`/${projectNumber}${routes.missionUpload}`),
        submitNewMission: (projectNumber: string, flightPlanIds: number[]) =>
            navigate(
                `/${projectNumber}${routes.missionEditor}/submit${flightPlanIds.length === 0 ? "" : "?"}${flightPlanIds.map((id) => `flight-plan-id=${id}`).join("&")}`,
            ),
        missionSelect: (projectNumber: string) => navigate(`/${projectNumber}${routes.missionSelect}`),
        editProject: (projectNumber: string, params?: { aoiId?: number }) => {
            const queryParams = new URLSearchParams()
            if (params?.aoiId != null) {
                queryParams.append("aoiId", params.aoiId.toString())
            }
            const paramsString = queryParams.toString()
            navigate(`/${projectNumber}/project-editor${paramsString.length === 0 ? "" : `?${paramsString}`}`)
        },
        createDataReceipt: () => navigate(routes.createDataReceipt),
        dataReceiptTable: () => navigate(routes.dataReceipts),
        dataReceiptEditor: (dataReceiptId?: string) => navigate(routes.editDataReceipt(dataReceiptId)),
        initializeProject: (projectNumber?: string) =>
            navigate(projectNumber == null ? routes.projectInit : `${routes.projectInit}/${projectNumber}`),
        searchDataReceipts: (filters?: DataReceiptSearchFitlersAndPaging) => {
            let goTo = routes.dataReceipts

            if (filters != null) {
                const params = getQueryParamString(filters, checkDataReceiptFilter)
                if (params.length > 0) {
                    goTo += `?${params}`
                }
            }

            location.pathname.includes(routes.dataReceipts) ? navigate(goTo, { replace: true }) : navigate(goTo)
        },
        searchSensorInstalls: (filters?: SensorInstallFiltersAndPaging, sensorId?: string) => {
            let baseRoute = routes.sensorInstallations
            if (sensorId != null) {
                baseRoute = routes.sensorInstallationsForSensor(sensorId)
            }

            let goTo = baseRoute
            if (filters != null) {
                const params = getQueryParamString<SensorInstallFiltersAndPaging>(filters, checkSensorInstallFilter)
                if (params.length > 0) {
                    goTo += `?${params}`
                }
            }
            location.pathname.includes(baseRoute) ? navigate(goTo, { replace: true }) : navigate(goTo)
        },
    }
}

// These are custom default filters that don't need to be in the query strings
const checkDataReceiptFilter = (key: string, value: unknown) =>
    (key !== "order" || value !== Order.desc) && (key !== "ordering" || value !== DataReceiptsOrder.DATE_RECEIVED)

const checkSensorInstallFilter = (key: string, value: unknown) =>
    (key !== "order" || value !== Order.desc) && (key !== "ordering" || value !== SensorInstallOrder.UPLOAD_DATE)

const getQueryParamString = <T extends object>(
    filters: T,
    customFilterCheck: (key: string, value: unknown) => boolean,
) => {
    const paramStrings = Object.fromEntries(
        Object.entries(filters)
            .filter(([key, value]) => {
                return (
                    value != null &&
                    (key !== "page" || value !== 0) &&
                    (key !== "page_size" || value !== nexDefaultPagination.pageSize) &&
                    customFilterCheck(key, value)
                )
            })
            .map(([key, value]) => [
                key,
                String(value), // Convert each value to a string
            ]),
    )
    return new URLSearchParams(paramStrings).toString()
}
